import React, { useEffect, useState, isValidElement, type PropsWithChildren } from 'react';
import { useProductStateMachine } from '../../../product-state-machine/use-product-state-machine';
import { ActiveProduct } from '../active-product';
import { type SearchItems } from '../result-types';
import {
	type CacheWarmingProps,
	EMPTY_SEARCH_ITEMS,
	type ResultSuppliers,
	type SearchItemsTransformerProps,
} from './result-provider-types';
import { ResultRenderer as DefaultResultRenderer } from './result-renderer/result-renderer';
import RegisterAnalytics from './analytics/register-analytics';
import { type AdvancedSearchFooterProps } from '../../../advanced-search-footer/advanced-search-footer';
import { type Workspace } from '../../../../common/search-config-provider/search-config-types';
import { FasterSearchFirstSection } from './faster-search-first-section';
import type {
	ResultRendererPropsComplete,
	CustomizedRendererChildFn,
	ResultRendererProps,
	ScreenSpecificProps,
} from './result-renderer/result-renderer-types';

const DefaultSearchItemsTransformer = ({
	preQueryItems,
	postQueryItems,
	productState,
	isFasterSearchDisabled,
	children,
}: SearchItemsTransformerProps) => {
	return isFasterSearchDisabled ? (
		<>
			{children({
				preQueryItems,
				postQueryItems,
				productState,
			})}
		</>
	) : (
		<FasterSearchFirstSection postQueryItems={postQueryItems} preQueryItems={preQueryItems}>
			{({ searchItems: fasterSearchItems }) => {
				const interleavedItems = {
					sections: [...fasterSearchItems.sections, ...postQueryItems.sections.slice(1)],
					size: postQueryItems.size,
				};

				return children({
					preQueryItems,
					postQueryItems: interleavedItems,
					productState,
				});
			}}
		</FasterSearchFirstSection>
	);
};

export interface ResultProviderProps {
	id: string;
	/**
	 * We don't want product state machine logic to kick for Conf and Jira.
	 * These legacy dialogs maintain the state within themselves in a very crude way.
	 * Hence we pass this flag to disable unnecessarily running the state machine when
	 * the old dialogs are active.
	 *
	 * It is only for legacy products. Any new implementation should not use this.
	 * Will be removed in [this PR](https://product-fabric.atlassian.net/browse/QS-2302)
	 */
	PLS_DONT_USE_skipDefaultStateManagement?: boolean;

	/**
	 * The workspaces to request search items from
	 */
	workspaces?: Workspace[];

	/**
	 * The result renderer used to display the retrieved results
	 */
	ResultRenderer?: React.FC<ResultRendererPropsComplete>;

	/**
	 * A component which transforms the search items that come from the product
	 * state machine. This came into existence because both analytics module and result renderer needed
	 * the results in the exact same order.
	 */
	SearchItemsTransformer?: React.FC<SearchItemsTransformerProps>;

	/**
	 * Is faster search disabled?
	 * This is used to disable faster search in the result.
	 */
	isFasterSearchDisabled?: boolean;
}

export type ResultProviderCompleteProps = ResultProviderProps &
	ResultSuppliers &
	Pick<ResultRendererProps, 'linkComponent'> &
	CacheWarmingProps &
	ScreenSpecificProps &
	CustomizedRendererChildFn &
	AdvancedSearchFooterProps;

// Visible only for testing
export const ResultProviderStateMachine: ({
	id,
	children,
	SearchItemsTransformer,
	ResultRenderer,
	isFasterSearchDisabled,
	...rest
}: ResultProviderCompleteProps) => React.JSX.Element = ({
	id,
	children,
	SearchItemsTransformer = DefaultSearchItemsTransformer,
	ResultRenderer = DefaultResultRenderer,
	isFasterSearchDisabled,
	...rest
}: ResultProviderCompleteProps) => {
	const [preQueryItems, setPreQueryItems] = useState<SearchItems>(EMPTY_SEARCH_ITEMS);
	const [postQueryItems, setPostQueryItems] = useState<SearchItems>(EMPTY_SEARCH_ITEMS);

	const { state, onRetryPreQuery, onRetryPostQuery } = useProductStateMachine({
		id,
		setPreQueryItems,
		setPostQueryItems,
		...rest,
	});

	return (
		<ActiveProduct id={id}>
			<SearchItemsTransformer
				preQueryItems={preQueryItems}
				postQueryItems={postQueryItems}
				productState={state}
				isFasterSearchDisabled={isFasterSearchDisabled}
			>
				{(args) => {
					const {
						preQueryItems: transformedPreQueryItems,
						postQueryItems: transformedPostQueryItems,
					} = args;

					return (
						<RegisterAnalytics
							preQueryItems={transformedPreQueryItems}
							postQueryItems={transformedPostQueryItems}
							productState={state}
						>
							<ResultRenderer
								{...rest}
								preQueryItems={transformedPreQueryItems}
								postQueryItems={transformedPostQueryItems}
								productState={state}
								onRetryPreQuery={onRetryPreQuery}
								onRetryPostQuery={onRetryPostQuery}
							>
								{children}
							</ResultRenderer>
						</RegisterAnalytics>
					);
				}}
			</SearchItemsTransformer>
		</ActiveProduct>
	);
};

export type ResultProviderLegacyProps = PropsWithChildren<
	CacheWarmingProps & Pick<ResultSuppliers, 'preQueryItemSupplier'>
>;
// Visible only for testing
export const ResultProviderLegacy: ({
	children,
	isPrefetchingEnabled,
	preQueryItemSupplier,
}: ResultProviderLegacyProps) => React.JSX.Element = ({
	children,
	isPrefetchingEnabled,
	preQueryItemSupplier,
}: ResultProviderLegacyProps) => {
	useEffect(() => {
		if (isPrefetchingEnabled) {
			preQueryItemSupplier({ sectionIds: [] }); // legacy products like jira and confluence dont use extensible way of rendering results.
		}
	}, [isPrefetchingEnabled, preQueryItemSupplier]);
	return <>{children}</>;
};

/**
 * This component provides the components with Pre, Post query results and the current state of the product.
 */
export const ResultProvider: ({
	children,
	id,
	PLS_DONT_USE_skipDefaultStateManagement,
	...rest
}: ResultProviderCompleteProps) => React.JSX.Element = ({
	children,
	id,
	PLS_DONT_USE_skipDefaultStateManagement,
	...rest
}: ResultProviderCompleteProps) => {
	return (
		<>
			{!PLS_DONT_USE_skipDefaultStateManagement ? (
				<ResultProviderStateMachine id={id} {...rest}>
					{children}
				</ResultProviderStateMachine>
			) : (
				<ResultProviderLegacy
					isPrefetchingEnabled={rest.isPrefetchingEnabled}
					preQueryItemSupplier={rest.preQueryItemSupplier}
				>
					<ActiveProduct id={id}>{isValidElement(children) ? children : null}</ActiveProduct>
				</ResultProviderLegacy>
			)}
		</>
	);
};
